import {useState, useEffect, useCallback, useContext, useRef, useMemo} from 'react';
import {doc, getDoc, setDoc, updateDoc} from 'firebase/firestore';

import {db} from '-/firebase';
import {UserContext} from '-/contexts/User';

import {DEFAULT_SHORTCUTS, MENU_GROUPS} from './menuConfig';

const DEBOUNCE_MS = 500;

export default function useMenuPreferences(allItems) {
    const {currentUser} = useContext(UserContext);
    const uid = currentUser?.uid;

    const [shortcuts, setShortcuts] = useState(DEFAULT_SHORTCUTS);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            return localStorage.getItem('menuCollapsed') === 'true';
        } catch (e) {
            return false;
        }
    });
    const [expandedGroups, setExpandedGroups] = useState(() => MENU_GROUPS.map(g => g.id));
    const [loaded, setLoaded] = useState(false);

    const debounceRef = useRef(null);
    const docRef = useMemo(() => uid ? doc(db, 'userPreferences', uid) : null, [uid]);

    // Read preferences on mount
    useEffect(() => {
        if (!docRef) {
            return;
        }

        let cancelled = false;

        getDoc(docRef).then(snap => {
            if (cancelled) {
                return;
            }

            if (snap.exists()) {
                const data = snap.data();
                if (data.menuShortcuts) {
                    setShortcuts(data.menuShortcuts);
                }
                if (data.menuCollapsed !== undefined) {
                    setIsCollapsed(data.menuCollapsed);
                    try { localStorage.setItem('menuCollapsed', String(data.menuCollapsed)); } catch (e) { /* */ }
                }
                if (data.menuExpandedGroups) {
                    setExpandedGroups(data.menuExpandedGroups);
                }
            }

            setLoaded(true);
        }).catch(() => {
            setLoaded(true);
        });

        return () => {
            cancelled = true;
        };
    }, [docRef]);

    // Filter shortcuts to only include items the user has access to
    const validShortcuts = useMemo(() => {
        if (!allItems) {
            return shortcuts;
        }
        const itemIds = new Set(allItems.map(i => i.id));
        return shortcuts.filter(s => itemIds.has(s));
    }, [shortcuts, allItems]);

    const persist = useCallback(updates => {
        if (!docRef) {
            return;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            try {
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    await updateDoc(docRef, updates);
                } else {
                    await setDoc(docRef, updates);
                }
            } catch (e) {
                // Silently fail — preferences are non-critical
            }
        }, DEBOUNCE_MS);
    }, [docRef]);

    const addShortcut = useCallback(itemId => {
        setShortcuts(prev => {
            if (prev.includes(itemId)) {
                return prev;
            }
            const next = [...prev, itemId];
            persist({menuShortcuts: next});
            return next;
        });
    }, [persist]);

    const removeShortcut = useCallback(itemId => {
        setShortcuts(prev => {
            const next = prev.filter(s => s !== itemId);
            persist({menuShortcuts: next});
            return next;
        });
    }, [persist]);

    const reorderShortcuts = useCallback(newOrder => {
        setShortcuts(newOrder);
        persist({menuShortcuts: newOrder});
    }, [persist]);

    const toggleCollapsed = useCallback(() => {
        setIsCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem('menuCollapsed', String(next)); } catch (e) { /* */ }
            persist({menuCollapsed: next});
            return next;
        });
    }, [persist]);

    const toggleGroup = useCallback(groupId => {
        setExpandedGroups(prev => {
            const next = prev.includes(groupId)
                ? prev.filter(g => g !== groupId)
                : [...prev, groupId];
            persist({menuExpandedGroups: next});
            return next;
        });
    }, [persist]);

    return {
        shortcuts: validShortcuts,
        isCollapsed,
        expandedGroups,
        loaded,
        addShortcut,
        removeShortcut,
        reorderShortcuts,
        toggleCollapsed,
        toggleGroup
    };
}
